import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { expressMutation, useExpressQuery } from '../../../../../../repository/react-query/useExpress';
import { useContext } from 'react';
import { NotificationContext } from '../../../../../../NotificationContext';
import { pluralize } from '../../../../../../../utils';

const baseQueryKey = 'dynamic-user-groups';

export const useGetUserGroupsV2 = ({ enabled = true } = {}) => {
    return useQuery({
        queryKey: [baseQueryKey, 'list'],
        queryFn: async () => {
            const userGroupResponse = await useExpressQuery({
                endpoint: '/user-groups',
                params: {
                    populate: { users: ['role', 'name', 'license_number', 'deleted_at'] },
                },
            })();

            return {
                userGroups: userGroupResponse,
            };
        },
        enabled,
    });
};

export const useGetUserGroupV2 = ({ id, enabled = true } = {}) => {
    return useQuery({
        queryKey: [baseQueryKey, 'detail', id],
        queryFn: useExpressQuery({ endpoint: `/user-groups/${id}` }),
        enabled: enabled && !!id,
    });
};

export const useCreateUserGroupV2 = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);

    return useMutation({
        mutationFn: (model) =>
            expressMutation({
                endpoint: '/user-groups',
                body: model,
                method: 'POST',
            }),
        onError: (_error) => {
            const errorMessage = 'Unable to create user group. If the problem persists please contact support.';
            showSnackbar({
                severity: 'error',
                message: errorMessage,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};

export const useUpdateUserGroupV2 = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);

    return useMutation({
        mutationFn: ({ id, model }) =>
            expressMutation({
                endpoint: `/user-groups/${id}`,
                body: model,
                method: 'PUT',
            }),
        onError: (_error) => {
            const errorMessage = 'Unable to update user group';
            showSnackbar({
                severity: 'error',
                message: errorMessage,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};

export const useDeleteUserGroupV2 = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);

    return useMutation({
        mutationFn: (id) =>
            expressMutation({
                endpoint: `/user-groups/${id}`,
                method: 'DELETE',
            }),
        onError: (_error) => {
            const errorMessage = 'Unable to delete user group';
            showSnackbar({
                severity: 'error',
                message: errorMessage,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};

/**
 * Preview user group - get users that would be included in a dynamic group
 */
export const usePreviewUserGroup = () => {
    const { showSnackbar } = useContext(NotificationContext);

    return useMutation({
        mutationFn: (previewData) =>
            expressMutation({
                endpoint: '/user-groups/preview',
                body: previewData,
                method: 'POST',
            }),
        onSuccess: ({ users }) => {
            if (users == null || !Array.isArray(users)) {
                showSnackbar({
                    severity: 'error',
                    message: 'Something went wrong while previewing the user group. Please try again or contact support if the issue persists.',
                });
                return new Error('Invalid preview data received');
            }

            if (users.length === 0) {
                showSnackbar({
                    severity: 'warning',
                    message: 'No users would be included in this group with the current criteria.',
                });
                return [];
            }

            showSnackbar({
                severity: 'success',
                message: `Preview complete: ${users.length.toLocaleString()} ${pluralize('user', users.length)} would be included in this group`,
            });
            return users;
        },
        onError: (_error) => {
            showSnackbar({ severity: 'error', message: 'Error previewing user group. Please try again or contact support if the issue persists.' });
        },
    });
};
